import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { ArrowClockwise, FloppyDisk, FilePdf, Warning, GraduationCap, Sparkle, CreditCard } from "@phosphor-icons/react";
import PatientForm from "@/components/triage/PatientForm";
import VitalsCard from "@/components/triage/VitalsCard";
import ScalesCard from "@/components/triage/ScalesCard";
import ProtocolPicker from "@/components/triage/ProtocolPicker";
import DiscriminatorGrid from "@/components/triage/DiscriminatorGrid";
import TriageCodePanel from "@/components/triage/TriageCodePanel";
import TakeoverTimer from "@/components/triage/TakeoverTimer";
import DebriefPanel from "@/components/triage/DebriefPanel";
import RequiredExamsAlert from "@/components/triage/RequiredExamsAlert";
import InterviewHintsPanel from "@/components/triage/InterviewHintsPanel";
import { PRIORITY_CODES, codeClasses } from "@/lib/api";
import { Siren, Eraser, Monitor } from "@phosphor-icons/react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const empty = () => ({
  patient: {}, arrival: {}, vitals: {}, scales: {},
  protocol: null, selectedDiscs: [], autoMatched: [], assignedCode: null, reason: "", notes: "",
});

export default function TriagePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [s, setS] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [kiosk, setKiosk] = useState(() => localStorage.getItem("tiger:kiosk") === "1");
  const [idleSec, setIdleSec] = useState(0);
  const [quota, setQuota] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const KIOSK_IDLE_LIMIT = 300; // seconds — auto-reset after 5 minutes of inactivity

  const loadQuota = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/quota");
      setQuota(data);
    } catch (err) {
      console.warn("[triage] quota load failed", err);
    }
  }, []);

  useEffect(() => { loadQuota(); }, [loadQuota]);

  useEffect(() => {
    document.documentElement.classList.toggle("kiosk-mode", kiosk);
    localStorage.setItem("tiger:kiosk", kiosk ? "1" : "0");
    return () => document.documentElement.classList.remove("kiosk-mode");
  }, [kiosk]);
  const toggleKiosk = () => setKiosk((v) => !v);

  // Idle auto-lock: reset the current triage after KIOSK_IDLE_LIMIT seconds of user inactivity
  useEffect(() => {
    if (!kiosk) { setIdleSec(0); return; }
    const bump = () => setIdleSec(0);
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const tick = setInterval(() => setIdleSec((v) => v + 1), 1000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(tick);
    };
  }, [kiosk]);

  useEffect(() => {
    if (!kiosk) return;
    if (idleSec >= KIOSK_IDLE_LIMIT) {
      setS(empty());
      setSavedId(null);
      setIdleSec(0);
      toast.info("Sessione ripristinata per inattività");
    }
  }, [idleSec, kiosk]);

  // Auto-evaluate against vitals/scales whenever they change
  useEffect(() => {
    if (!s.protocol?.protocol_id) {
      if (s.autoMatched.length) setS((cur) => ({ ...cur, autoMatched: [] }));
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/engine/evaluate", {
          protocol_id: s.protocol.protocol_id,
          vitals: s.vitals,
          scales: s.scales,
          selected_discriminators: s.selectedDiscs,
        }, { signal: ctrl.signal });
        setS((cur) => ({ ...cur, autoMatched: data.auto_matched || [] }));
      } catch (err) {
        if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
          console.warn("[triage] engine evaluate failed", err);
        }
      }
    }, 300);
    return () => { ctrl.abort(); clearTimeout(t); };
  }, [s.protocol?.protocol_id, s.vitals, s.scales, s.selectedDiscs]);

  const combinedDiscs = useMemo(() => {
    const seen = new Set(s.autoMatched.map((d) => d.id));
    return [...s.autoMatched, ...s.selectedDiscs.filter((d) => !seen.has(d.id))];
  }, [s.autoMatched, s.selectedDiscs]);

  const suggested = useMemo(() => {
    const codes = combinedDiscs.map((d) => d.code).filter(Boolean);
    if (codes.length === 0) return null;
    const order = ["rosso", "arancione", "azzurro", "verde", "bianco"];
    return codes.slice().sort((a, b) => order.indexOf(a) - order.indexOf(b))[0];
  }, [combinedDiscs]);

  const resetAll = () => { setS(empty()); setSavedId(null); toast.success("Triage azzerato"); };

  const autoMatchedIds = useMemo(() => new Set(s.autoMatched.map((d) => d.id)), [s.autoMatched]);

  const toggleDisc = (d) => {
    if (autoMatchedIds.has(d.id)) {
      toast.info("Discriminatore selezionato automaticamente in base ai parametri; modificarli per rimuoverlo.");
      return;
    }
    setS((cur) => {
      const exists = cur.selectedDiscs.find((x) => x.id === d.id);
      const next = exists ? cur.selectedDiscs.filter((x) => x.id !== d.id) : [...cur.selectedDiscs, d];
      return { ...cur, selectedDiscs: next };
    });
  };

  const save = async () => {
    if (!s.patient.cognome && !s.patient.nome && !s.patient.accesso_ps) {
      toast.error("Inserire almeno cognome, nome o numero accesso PS");
      return;
    }
    if (!s.assignedCode) {
      toast.error("Assegnare un codice di priorità prima di salvare");
      return;
    }
    // Client-side quota gate (server enforces the same rule)
    if (quota && !quota.unlimited && quota.remaining === 0) {
      setUpgradeOpen(true);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/triages", {
        patient: s.patient,
        arrival: s.arrival,
        vitals: s.vitals,
        scales: s.scales,
        protocol_id: s.protocol?.protocol_id,
        protocol_name: s.protocol?.name,
        protocol_version: s.protocol?.version,
        selected_discriminators: combinedDiscs,
        suggested_code: suggested,
        assigned_code: s.assignedCode || suggested,
        reason_for_change: s.reason,
        notes: s.notes,
      });
      setSavedId(data.triage_id);
      toast.success("Simulazione registrata");
      loadQuota();
    } catch (e) {
      const status = e?.response?.status;
      const payload = e?.response?.data?.detail;
      if (status === 402) {
        setUpgradeOpen(true);
        loadQuota();
      } else {
        toast.error(typeof payload === "string" ? payload : (payload?.message || "Errore durante il salvataggio"));
      }
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = () => {
    if (!savedId) { toast.error("Registra il triage prima di generare il PDF"); return; }
    window.open(`${API}/triages/${savedId}/pdf`, "_blank");
  };

  const clearSelection = () => {
    setS((cur) => ({ ...cur, selectedDiscs: [], assignedCode: null, reason: "" }));
    toast.success("Selezione azzerata");
  };

  const setAssignedCode = (code) => {
    setS((cur) => {
      const nowHHMM = new Date().toTimeString().slice(0, 5);
      // Auto-fill ora_presa when the nurse first assigns any code
      const arrival = cur.arrival.ora_presa ? cur.arrival : { ...cur.arrival, ora_presa: nowHHMM };
      if (!cur.arrival.ora_presa) toast.success(`Ora presa in carico impostata a ${nowHHMM}`);
      return { ...cur, assignedCode: code, arrival };
    });
  };

  const activateEmergency = () => {
    if (!s.protocol) {
      toast.error("Seleziona prima un protocollo");
      return;
    }
    const reds = (s.protocol.discriminators || []).filter((d) => d.code === "rosso" && d.active !== false);
    if (reds.length === 0) {
      toast.error("Nessun discriminatore rosso disponibile per questo protocollo");
      return;
    }
    setS((cur) => {
      const seen = new Set(cur.selectedDiscs.map((x) => x.id));
      const merged = [...cur.selectedDiscs, ...reds.filter((d) => !seen.has(d.id))];
      const nowHHMM = new Date().toTimeString().slice(0, 5);
      const arrival = cur.arrival.ora_presa ? cur.arrival : { ...cur.arrival, ora_presa: nowHHMM };
      return { ...cur, selectedDiscs: merged, assignedCode: "rosso", arrival };
    });
    toast.success(`Emergenza attivata: ${reds.length} discriminatori rossi selezionati`);
  };

  const selectionByCode = useMemo(() => {
    const map = {};
    for (const c of PRIORITY_CODES) map[c.code] = [];
    for (const d of combinedDiscs) {
      if (map[d.code]) map[d.code].push(d);
    }
    return map;
  }, [combinedDiscs]);

  return (
    <div>
      {quota && !quota.unlimited && (
        <div
          data-testid="quota-banner"
          className={`px-6 py-3 border-b flex items-center justify-between gap-4 flex-wrap text-sm ${
            quota.remaining === 0
              ? "bg-red-50 border-red-200 text-red-900"
              : quota.remaining <= 2
              ? "bg-amber-50 border-amber-300 text-amber-900"
              : "bg-slate-50 border-slate-200 text-slate-700"
          }`}
        >
          <div className="flex items-center gap-2">
            <GraduationCap size={18} weight="fill" />
            <span data-testid="quota-remaining-text">
              {quota.remaining === 0
                ? "Hai esaurito le 5 simulazioni gratuite."
                : `${quota.remaining} simulazioni gratuite rimanenti (${quota.used}/${quota.limit} usate).`}
            </span>
          </div>
          <Button
            data-testid="quota-upgrade-button"
            size="sm"
            className="min-h-[36px] bg-slate-900 hover:bg-slate-800 text-white"
            onClick={() => nav("/billing")}
          >
            <Sparkle size={16} weight="fill" className="mr-1.5" /> Passa a Piano Formatore
          </Button>
        </div>
      )}
      <div className="sticky top-0 z-30 bg-white sticky-header border-b border-slate-200 px-6 py-4 flex items-center justify-between kiosk:py-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Simulazione corrente</div>
            <div className="font-heading text-xl font-semibold kiosk:text-3xl">
              {s.patient.cognome || s.patient.nome
                ? `${s.patient.cognome || ""} ${s.patient.nome || ""}`.trim()
                : "Nuovo caso didattico"}
              {s.patient.accesso_ps && <span className="ml-3 text-sm font-mono text-slate-500">#{s.patient.accesso_ps}</span>}
            </div>
          </div>
          {s.arrival.ora_accettazione && (
            <TakeoverTimer
              oraAccettazione={s.arrival.ora_accettazione}
              oraPresa={s.arrival.ora_presa}
              assignedCode={s.assignedCode}
            />
          )}
          {kiosk && idleSec > KIOSK_IDLE_LIMIT - 60 && (
            <div data-testid="kiosk-idle-warning" className="inline-flex items-center gap-2 px-3 py-2 rounded-md border bg-amber-50 text-amber-800 border-amber-300">
              <div className="text-[10px] font-semibold uppercase tracking-widest">Auto-reset in</div>
              <div className="font-mono text-lg font-semibold">{KIOSK_IDLE_LIMIT - idleSec}s</div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Button
            onClick={activateEmergency}
            data-testid="emergency-button"
            className="min-h-[44px] bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/20"
          >
            <Siren size={18} weight="fill" className="mr-2" /> EMERGENZA — attiva tutti i Rossi
          </Button>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="outline"
                    onClick={save}
                    disabled={saving || !s.assignedCode}
                    data-testid="save-triage-button"
                    className="min-h-[44px]"
                  >
                    <FloppyDisk size={18} className="mr-2" /> {saving ? "Salvataggio…" : "Registra"}
                  </Button>
                </span>
              </TooltipTrigger>
              {!s.assignedCode && (
                <TooltipContent data-testid="save-tooltip">
                  Assegna un codice di priorità prima di registrare il triage.
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          <Button onClick={downloadPdf} disabled={!savedId} data-testid="pdf-triage-button" className="min-h-[44px] bg-slate-900 hover:bg-slate-800">
            <FilePdf size={18} className="mr-2" /> Genera PDF
          </Button>
          <Button
            variant="outline"
            onClick={toggleKiosk}
            data-testid="kiosk-toggle"
            className="min-h-[44px]"
            title="Modalità tablet / kiosk"
          >
            <Monitor size={18} className="mr-2" /> {kiosk ? "Esci Kiosk" : "Kiosk"}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" data-testid="reset-button" className="min-h-[44px]">
                <ArrowClockwise size={18} className="mr-2" /> RESET
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <Warning size={22} className="text-red-600" /> Confermi l'azzeramento?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  L'operazione cancella tutti i dati del triage corrente. Non è reversibile.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="reset-cancel">Annulla</AlertDialogCancel>
                <AlertDialogAction data-testid="reset-confirm" onClick={resetAll}>Azzera</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <PatientForm
          patient={s.patient} arrival={s.arrival}
          onPatient={(p) => setS({ ...s, patient: p })}
          onArrival={(a) => setS({ ...s, arrival: a })}
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 space-y-6">
            <VitalsCard vitals={s.vitals} onChange={(v) => setS({ ...s, vitals: v })} />
            <ScalesCard scales={s.scales} onChange={(sc) => setS({ ...s, scales: sc })} />
          </div>
          <div className="lg:col-span-7 space-y-6">
            <ProtocolPicker selected={s.protocol} onSelect={(p) => setS({ ...s, protocol: p, selectedDiscs: [], autoMatched: [] })} />
            {s.protocol && (
              <RequiredExamsAlert protocol={s.protocol} />
            )}
            {s.protocol && (
              <InterviewHintsPanel protocol={s.protocol} />
            )}
            <DiscriminatorGrid
              protocol={s.protocol}
              selected={combinedDiscs}
              autoMatchedIds={autoMatchedIds}
              onToggle={toggleDisc}
              onClearAll={clearSelection}
              selectionCount={s.selectedDiscs.length}
            />
            <TriageCodePanel
              suggested={suggested}
              assigned={s.assignedCode}
              onAssign={setAssignedCode}
              reason={s.reason}
              onReason={(r) => setS({ ...s, reason: r })}
            />
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <h3 className="font-heading text-lg font-semibold mb-2">Note</h3>
              <Textarea
                data-testid="notes-input"
                rows={4}
                value={s.notes}
                onChange={(e) => setS({ ...s, notes: e.target.value })}
                placeholder="Note libere per il report"
              />
            </div>
            {savedId && (
              <DebriefPanel triageId={savedId} />
            )}
          </div>
        </div>

        {combinedDiscs.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="selection-summary">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg font-semibold">Anteprima selezione</h3>
              <div className="text-xs text-slate-500">
                {combinedDiscs.length} discriminatori totali · codice suggerito
                <span className={`ml-2 chip-code text-xs ${suggested ? codeClasses(suggested) : "bg-slate-200 text-slate-600"}`}>
                  {suggested ? suggested.toUpperCase() : "—"}
                </span>
              </div>
            </div>
            <div className="space-y-2">
              {PRIORITY_CODES.map((c) => {
                const items = selectionByCode[c.code] || [];
                if (items.length === 0) return null;
                return (
                  <div key={c.code} className="flex items-start gap-3">
                    <span className={`chip-code text-xs shrink-0 mt-0.5 ${c.classes}`}>{c.label}</span>
                    <div className="text-xs font-mono text-slate-400 shrink-0 mt-1">{items.length}</div>
                    <div className="flex-1 flex flex-wrap gap-1">
                      {items.map((d) => (
                        <span key={d.id}
                              data-testid={`summary-item-${d.id}`}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 text-xs text-slate-700 border border-slate-200 max-w-md truncate"
                              title={d.name}>
                          {d.name}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <AlertDialogContent data-testid="upgrade-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CreditCard size={22} weight="fill" className="text-amber-600" />
              Limite simulazioni raggiunto
            </AlertDialogTitle>
            <AlertDialogDescription>
              Hai completato le 5 simulazioni gratuite del modulo formativo. Passa al{" "}
              <b>Piano Formatore</b> (€3,99/settimana o €190/anno) per continuare la tua formazione
              con simulazioni illimitate, storico completo e PDF didattici.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="upgrade-dialog-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="upgrade-dialog-confirm"
              className="bg-slate-900 hover:bg-slate-800"
              onClick={() => { setUpgradeOpen(false); nav("/billing"); }}
            >
              Scopri i piani
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
